import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Shield,
  Search,
  Filter,
  AlertTriangle,
  CheckCircle,
  Clock,
  User,
  FileText,
  Activity,
  Download,
} from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { api } from '@/services/api';

interface AuditLog {
  id: string;
  userId: string;
  action: string;
  entityType: string;
  entityId: string;
  oldValues?: any;
  newValues?: any;
  ipAddress: string;
  userAgent: string;
  createdAt: string;
  sequenceNumber: number;
  previousHash: string;
  hash: string;
  user?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
}

interface AuditStats {
  totalLogs: number;
  actionCounts: Record<string, number>;
  entityTypeCounts: Record<string, number>;
  userCounts: Record<string, number>;
  recentActivity: AuditLog[];
  timeRange: {
    earliest: string;
    latest: string;
  };
}

const AuditPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showIntegrity, setShowIntegrity] = useState(false);

  // Fetch audit logs
  const { data: logsData, isLoading: isLoadingLogs } = useQuery<{
    logs: AuditLog[];
    pagination: { limit: number; offset: number; total: number };
  }>({
    queryKey: ['audit', 'logs', searchTerm, actionFilter, entityTypeFilter, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('userId', searchTerm);
      if (actionFilter) params.append('action', actionFilter);
      if (entityTypeFilter) params.append('entityType', entityTypeFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      
      const res = await api.get(`/api/audit/logs?${params}`);
      return res.data;
    },
  });

  // Fetch audit stats
  const { data: statsData } = useQuery<AuditStats>({
    queryKey: ['audit', 'stats'],
    queryFn: async () => {
      const res = await api.get('/api/audit/stats');
      return res.data?.data?.stats || res.data?.stats;
    },
  });

  // Fetch integrity status
  const { data: integrityData } = useQuery({
    queryKey: ['audit', 'integrity'],
    queryFn: async () => {
      const res = await api.get('/api/audit/integrity');
      return res.data;
    },
    });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-MW', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getActionColor = (action: string) => {
    if (action.includes('create') || action.includes('post')) return 'text-green-700 bg-green-100';
    if (action.includes('update') || action.includes('put')) return 'text-blue-700 bg-blue-100';
    if (action.includes('delete') || action.includes('remove')) return 'text-red-700 bg-red-100';
    return 'text-gray-700 bg-gray-100';
  };

  const getEntityTypeIcon = (entityType: string) => {
    switch (entityType.toLowerCase()) {
      case 'product': return <FileText className="h-4 w-4" />;
      case 'salesinvoice': return <Activity className="h-4 w-4" />;
      case 'user': return <User className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Trail</h1>
          <p className="mt-1 text-sm text-gray-600">
            System activity logs with tamper evidence
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant={showIntegrity ? "secondary" : "ghost"}
            onClick={() => setShowIntegrity(!showIntegrity)}
            className="flex items-center"
          >
            <Shield className="h-4 w-4 mr-2" />
            {showIntegrity ? 'Hide' : 'Show'} Integrity
          </Button>
          <Button variant="ghost" className="flex items-center">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Integrity Status */}
      {showIntegrity && integrityData && (
        <div className={`card border-l-4 ${
          integrityData.integrity.isValid ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
        }`}>
          <div className="card-body">
            <div className="flex items-center">
              {integrityData.integrity.isValid ? (
                <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
              )}
              <div>
                <h3 className={`font-semibold ${
                  integrityData.integrity.isValid ? 'text-green-900' : 'text-red-900'
                }`}>
                  Audit Trail Integrity: {integrityData.integrity.isValid ? 'Valid' : 'Compromised'}
                </h3>
                <p className="text-sm text-gray-600">
                  Total logs: {integrityData.integrity.totalLogs} | 
                  Verified: {formatDate(integrityData.verifiedAt)}
                </p>
                {!integrityData.integrity.isValid && integrityData.integrity.tamperedLogs.length > 0 && (
                  <p className="text-sm text-red-600 mt-1">
                    {integrityData.integrity.tamperedLogs.length} tampered logs detected
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      {statsData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-blue-100 rounded-lg p-3">
                  <Activity className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Logs</p>
                  <p className="text-2xl font-bold text-gray-900">{statsData.totalLogs.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-green-100 rounded-lg p-3">
                  <User className="h-6 w-6 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active Users</p>
                  <p className="text-2xl font-bold text-gray-900">{Object.keys(statsData.userCounts).length}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-purple-100 rounded-lg p-3">
                  <FileText className="h-6 w-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Entity Types</p>
                  <p className="text-2xl font-bold text-gray-900">{Object.keys(statsData.entityTypeCounts).length}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-orange-100 rounded-lg p-3">
                  <Clock className="h-6 w-6 text-orange-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Time Range</p>
                  <p className="text-sm font-bold text-gray-900">
                    {statsData.timeRange.earliest ? 
                      new Date(statsData.timeRange.earliest).toLocaleDateString() : 
                      'No data'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search by user ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                icon={<Search className="h-4 w-4 text-gray-400" />}
              />
            </div>
            <div className="flex-1">
              <Input
                placeholder="Filter by action..."
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                icon={<Filter className="h-4 w-4 text-gray-400" />}
              />
            </div>
            <div className="flex-1">
              <Input
                placeholder="Filter by entity type..."
                value={entityTypeFilter}
                onChange={(e) => setEntityTypeFilter(e.target.value)}
                icon={<Filter className="h-4 w-4 text-gray-400" />}
              />
            </div>
            <div className="flex-1">
              <Input
                type="date"
                placeholder="Start date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="flex-1">
              <Input
                type="date"
                placeholder="End date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Audit Logs Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Entity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    IP Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Hash
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoadingLogs ? (
                  <tr>
                    <td className="px-6 py-6 text-sm text-gray-600" colSpan={6}>
                      Loading audit logs...
                    </td>
                  </tr>
                ) : logsData?.logs.length === 0 ? (
                  <tr>
                    <td className="px-6 py-6 text-sm text-gray-600" colSpan={6}>
                      No audit logs found
                    </td>
                  </tr>
                ) : (
                  logsData?.logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(log.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.user ? (
                          <div>
                            <div className="font-medium">{log.user.firstName} {log.user.lastName}</div>
                            <div className="text-gray-500">{log.user.email}</div>
                          </div>
                        ) : (
                          <span className="text-gray-500">Unknown User</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          {getEntityTypeIcon(log.entityType)}
                          <span className="ml-2">{log.entityType}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {log.ipAddress}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs font-mono text-gray-600">{log.hash}</span>
                          {log.previousHash !== '0000000000000000000000000000000000000000' && (
                            <div className="w-2 h-2 bg-green-400 rounded-full" title="Linked to previous log"></div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditPage;
